import PDFViewer from '@/components/PDFViewer'
import { getOrderReportById } from '@/features/report/services/reportClient'
import { logger } from '@/sentry.config'
import type { OrderReportResponse } from '@/types/OrderReportTypes'
import type { OrderFormInfoData } from '@/types/RequisitionFormTypes'
import { ChevronDown } from 'flowbite-react-icons/outline'
import { useEffect, useMemo, useRef, useState } from 'react'

export type OrderResultsPdfPanelProps = {
  orderId?: string
  orderData?: OrderFormInfoData
  parentReports: OrderReportResponse[]
  parentReportsLoading: boolean
  parentReportsError: string | null
  onReportChange?: (reportId: string) => void
}

type SortKey = {
  generatedAtMs: number | null
  version: number | null
}

const parseSortKey = (report: OrderReportResponse): SortKey => {
  const generatedAtMs = report.generated_at ? new Date(report.generated_at).getTime() : NaN
  const safeGeneratedAtMs = Number.isFinite(generatedAtMs) && generatedAtMs > 0 ? generatedAtMs : null
  const version = report.version ?? null
  return { generatedAtMs: safeGeneratedAtMs, version }
}

const formatReportDate = (dateString?: string | null) => {
  if (!dateString) return 'Unknown date'
  const ms = new Date(dateString).getTime()
  if (!Number.isFinite(ms) || ms <= 0) return 'Unknown date'
  return new Date(ms).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const formatReportLabel = (report: OrderReportResponse, isLatest: boolean) => {
  const versionLabel =
    report.version !== null && report.version !== undefined ? `Version ${report.version}` : 'Version'
  const dateLabel = formatReportDate(report.generated_at)
  return `${isLatest ? 'Latest — ' : ''}${versionLabel} • ${dateLabel}`
}

function pdfFilenameForReport(report: OrderReportResponse): string {
  const v = report.version !== null && report.version !== undefined ? String(report.version) : 'report'
  return `Lab-Report-v${v}.pdf`
}

/** Inline PDF results view for orgs without structured guidance (matches PDF-first results tab). */
export function OrderResultsPdfPanel({
  orderId,
  orderData,
  parentReports,
  parentReportsLoading,
  parentReportsError,
  onReportChange,
}: OrderResultsPdfPanelProps) {
  const [selectedReportId, setSelectedReportId] = useState('')
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [pdfError, setPdfError] = useState<string | null>(null)

  const callerFirst = orderData?.orderingProvider?.name?.firstName ?? ''
  const callerLast = orderData?.orderingProvider?.name?.lastName ?? ''
  const caller = useMemo(() => {
    const combined = `${callerFirst} ${callerLast}`.trim()
    return combined || 'spa'
  }, [callerFirst, callerLast])

  const sortedReports = useMemo(() => {
    const next = [...parentReports]
    next.sort((a, b) => {
      const aKey = parseSortKey(a)
      const bKey = parseSortKey(b)
      if (aKey.generatedAtMs !== null || bKey.generatedAtMs !== null) {
        const aMs = aKey.generatedAtMs ?? -1
        const bMs = bKey.generatedAtMs ?? -1
        if (aMs !== bMs) return bMs - aMs
      }
      if (aKey.version !== null || bKey.version !== null) {
        const aV = aKey.version ?? -1
        const bV = bKey.version ?? -1
        if (aV !== bV) return bV - aV
      }
      return a.id.localeCompare(b.id)
    })
    return next
  }, [parentReports])

  const latestReportId = sortedReports[0]?.id ?? ''
  const hasReports = sortedReports.length > 0

  useEffect(() => {
    if (!latestReportId) {
      setSelectedReportId('')
      return
    }
    setSelectedReportId((prev) => (prev && sortedReports.some((r) => r.id === prev) ? prev : latestReportId))
  }, [latestReportId, sortedReports])

  const onReportChangeRef = useRef(onReportChange)
  useEffect(() => {
    onReportChangeRef.current = onReportChange
  }, [onReportChange])

  useEffect(() => {
    if (!selectedReportId) return
    onReportChangeRef.current?.(selectedReportId)
  }, [selectedReportId])

  const currentPdfRequestRef = useRef<string | null>(null)

  useEffect(() => {
    if (!orderId || !selectedReportId) {
      setPdfBytes(null)
      setPdfLoading(false)
      setPdfError(null)
      currentPdfRequestRef.current = null
      return
    }

    const requestId = `${orderId}-${selectedReportId}-${caller}`
    currentPdfRequestRef.current = requestId

    const run = async () => {
      try {
        setPdfLoading(true)
        setPdfError(null)
        const bytes = await getOrderReportById(orderId, selectedReportId, caller)
        if (currentPdfRequestRef.current !== requestId) return
        setPdfBytes(bytes)
      } catch (error) {
        if (currentPdfRequestRef.current !== requestId) return
        logger.error('Failed to fetch report PDF', {
          orderId,
          reportId: selectedReportId,
          error: error instanceof Error ? error.message : String(error),
        })
        setPdfError(error instanceof Error ? error.message : 'Failed to load report PDF.')
        setPdfBytes(null)
      } finally {
        if (currentPdfRequestRef.current === requestId) {
          setPdfLoading(false)
        }
      }
    }

    void run()

    return () => {
      currentPdfRequestRef.current = null
    }
  }, [orderId, selectedReportId, caller])

  const selectedReport = useMemo(
    () => sortedReports.find((r) => r.id === selectedReportId) ?? null,
    [sortedReports, selectedReportId],
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
        <div>
          <p className="text-sm text-text-body dark:text-gray-400">Select a report version to view the PDF.</p>
        </div>
        <div className="min-w-0 shrink-0 sm:max-w-md sm:flex-1">
          <label htmlFor="order-results-pdf-version" className="sr-only">
            Report version
          </label>
          <div className="relative">
            <select
              id="order-results-pdf-version"
              className="w-full appearance-none rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 pl-3 pr-10 py-2.5 text-sm font-medium text-text-heading dark:text-white shadow-xs focus:border-border-brand dark:focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-600"
              value={selectedReportId}
              disabled={!hasReports || parentReportsLoading}
              onChange={(e) => setSelectedReportId(e.target.value)}
            >
              {!hasReports ? <option value="">No reports</option> : null}
              {sortedReports.map((report) => (
                <option key={report.id} value={report.id}>
                  {formatReportLabel(report, report.id === latestReportId)}
                </option>
              ))}
            </select>
            <ChevronDown
              className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-body dark:text-gray-400"
              aria-hidden
            />
          </div>
        </div>
      </div>

      {parentReportsLoading ? (
        <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-text-body dark:text-gray-400 shadow-sm">
          Loading reports…
        </div>
      ) : parentReportsError ? (
        <div className="rounded-base border border-border-error-subtle dark:border-error-700 bg-error-50 dark:bg-gray-900 p-6 text-sm text-text-fg-error-strong dark:text-error-100 shadow-sm">
          {parentReportsError}
        </div>
      ) : !hasReports ? (
        <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-text-body dark:text-gray-400 shadow-sm">
          No reports available for this order.
        </div>
      ) : (
        <div className="min-h-[min(70vh,720px)] overflow-hidden rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm">
          {pdfLoading ? (
            <div className="flex items-center justify-center p-10 text-sm text-text-body dark:text-gray-400">
              Loading report PDF…
            </div>
          ) : pdfError ? (
            <div className="p-6 text-sm text-text-fg-error-strong dark:text-error-100">{pdfError}</div>
          ) : pdfBytes && selectedReport ? (
            <PDFViewer
              pdfBytes={pdfBytes}
              filename={pdfFilenameForReport(selectedReport)}
              className="min-h-[min(70vh,720px)]"
            />
          ) : (
            <div className="p-6 text-sm text-text-body dark:text-gray-400">No PDF available.</div>
          )}
        </div>
      )}
    </div>
  )
}
